// app/javascript/controllers/stripe_payment_controller.js
import { Controller } from "@hotwired/stimulus";
import { loadStripe } from "@stripe/stripe-js";

/**
 * Stimulus controller that mounts Stripe's Payment Element and keeps it in
 * sync whenever the cart total changes (discounts applied, items added/removed).
 *
 * ▶ Handles the `{ emptyCart: true }` flag returned by the back‑end, so we
 *   don't try to initialise or refresh Stripe when the cart is empty or $0.
 * ▶ Uses **POST /setup_checkout** and **PATCH /update_checkout** (no more GET),
 *   aligning with the new routes and avoiding cached side‑effects.
 * ▶ `jsonFetch` now injects the Rails authenticity token automatically for any
 *   non‑GET request, so CSRF protection stays happy.
 */
export default class extends Controller {
  static targets = ["paymentElement", "error", "spinner"];

  /* --------------------------------------------------------- lifecycle */

  connect() {
    this.element.addEventListener("discount:applied", this.updatePaymentIntent.bind(this));
    this.element.addEventListener("discount:removed", this.updatePaymentIntent.bind(this));

    // Fired by cart_button_controller after add/remove.
    window.addEventListener("payment-intent-updated", (e) => {
      const { clientSecret } = e.detail;
      this.refreshElements(clientSecret);
    });

    // Fired when the cart becomes empty so we hide the Payment Element.
    window.addEventListener("cart-empty", () => this.resetElements());

    this.bootstrapStripe();
  }

  /* ----------------------------------------------------------- helpers */

  getCsrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content;
  }

  async jsonFetch(url, opts = {}) {
    const method = opts.method?.toUpperCase() || "GET";

    // Inject authenticity token on any mutating request (POST/PATCH etc.).
    const defaultHeaders =
        method === "GET"
            ? {}
            : {
              "Content-Type": "application/json",
              "X-CSRF-Token": this.getCsrfToken()
            };

    const res = await fetch(url, {
      headers: { ...defaultHeaders, ...(opts.headers || {}) },
      ...opts
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return res.json();
  }

  /* ------------------------------------------------------- core logic */

  async bootstrapStripe() {
    try {
      const result = await this.fetchClientSecret();

      if (result?.emptyCart) return; // nothing to pay yet

      if (result?.clientSecret && result?.stripeAccountId) {
        await this.initializeStripe(result.clientSecret, result.stripeAccountId);
      } else {
        console.error("Missing client secret or Stripe account ID");
      }
    } catch (err) {
      console.error(err);
    }
  }

  async fetchClientSecret() {
    try {
      const json = await this.jsonFetch("/setup_checkout", { method: "POST" });
      if (json.emptyCart) return { emptyCart: true };

      return {
        clientSecret:    json.clientSecret,
        stripeAccountId: json.stripeAccountId,
        paymentIntentId: json.paymentIntentId,
        emptyCart:       false
      };
    } catch (err) {
      console.error("Error fetching client secret:", err);
      return null;
    }
  }

  async initializeStripe(clientSecret, stripeAccountId) {
    this.stripe = await loadStripe(process.env.STRIPE_PUBLISHABLE, {
      stripeAccount: stripeAccountId
    });
    this.refreshElements(clientSecret);
  }

  /**
   * Mounts or re‑mounts the Payment Element.
   */
  refreshElements(clientSecret) {
    if (!this.stripe) return;
    this.clientSecret = clientSecret;

    if (this.hasPaymentElementTarget) this.paymentElementTarget.innerHTML = "";

    const elements = this.stripe.elements({
      clientSecret: this.clientSecret,
      layout: { type: "tabs", defaultCollapsed: false }
    });
    const paymentElement = elements.create("payment");
    if (this.hasPaymentElementTarget) paymentElement.mount(this.paymentElementTarget);

    this.elements = elements;
  }

  resetElements() {
    if (this.hasPaymentElementTarget) this.paymentElementTarget.innerHTML = "";
    this.elements = null;
    this.clientSecret = null;
  }

  /* -------------------------------------------------- server interaction */

  async updatePaymentIntent() {
    try {
      if (this.hasSpinnerTarget) this.spinnerTarget.classList.remove("hidden");

      const data = await this.jsonFetch("/update_checkout", { method: "PATCH" });
      if (data.emptyCart) {
        this.resetElements();
        window.dispatchEvent(new CustomEvent("cart-empty"));
        return;
      }

      if (data.clientSecret) this.refreshElements(data.clientSecret);
    } catch (err) {
      console.error("Error updating PaymentIntent:", err);
      if (this.hasErrorTarget) this.errorTarget.textContent = err.message;
    } finally {
      if (this.hasSpinnerTarget) this.spinnerTarget.classList.add("hidden");
    }
  }

  /* --------------------------------------------------- payment capture */

  async submitPayment(event) {
    event.preventDefault();
    const btn = this.element.querySelector(".pay-now-btn");
    if (btn) btn.disabled = true;

    try {
      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        confirmParams: { return_url: `${window.location.origin}/order_success` }
      });

      if (error) {
        if (this.hasErrorTarget) this.errorTarget.textContent = error.message;
        console.error("Payment error:", error.message);
      }
    } catch (err) {
      console.error("Payment exception:", err);
    } finally {
      if (btn) btn.disabled = false;
    }
  }
}
